import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, IconLocked, tokens } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { CART_PLACE_ORDER_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import { usePaymentMethodStore } from '../../../payment';
import { PaymentOrchestrator } from '../../../payment/core/payment-orchestrator';
import { useBuildPipelineContext } from '../../../payment/hooks/use-build-pipeline-context';
import type { PaymentPipelineError, PaymentPipelineResult } from '../../../payment/payment-types';
import { useCartStore } from '../../../state/cart-store';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';
import { MAX_TIP_AMOUNT_CENTS, useTipStore } from '../../../state/tip-store';
import { logCartPlaceOrder, logPaymentRetry } from '../../../utils/logging-utils';

import { getPlaceOrderButtonText } from './get-place-order-button-text';
import { handlePlaceOrder } from './handle-place-order';
import { PaymentErrorAlert, PricingAlert } from './payment-alerts';

export default function PlaceOrderButton() {
  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentError, setCurrentError] = useState<PaymentPipelineError | null>(null);
  const lastResultRef = useRef<PaymentPipelineResult | null>(null);
  const orchestratorRef = useRef<PaymentOrchestrator>(new PaymentOrchestrator());

  // Store hooks
  const cartEntries = useCartStore(s => s.cartEntries);
  const isPaymentReady = usePaymentMethodStore(state => state.isPaymentReady());
  const otherTipValue = useTipStore(state => state.otherTipValue);
  const selectedTip = useTipStore(state => state.selectedTip);
  const { buildPipelineContext } = useBuildPipelineContext();
  const { setShouldSkipPriceOrder, serverOrderLoading, serverOrderError, serverOrder } =
    useCartContext();
  const { emptyCart, orderPhoneNumber, isDelivery } = useOrderContext();
  const { isOfferValidationError } = useLoyaltyContext();
  const currentState = usePaymentFlowStore(state => state.currentState);
  const setCurrentState = usePaymentFlowStore(state => state.setCurrentState);
  const resetPaymentFlowStore = usePaymentFlowStore(state => state.resetPaymentFlowStore);
  const resetPaymentMethodStore = usePaymentMethodStore(state => state.resetPaymentMethodStore);
  const retryInfo = usePaymentFlowStore(state => state.retryInfo);
  const { logRBIEvent } = useCRMEventsContext();

  const numericValidation = new RegExp(
    /^(\+?1 ?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/
  );
  const validPhoneNumber = numericValidation.test(orderPhoneNumber);

  // All Button States
  const serverOrderProcessing =
    serverOrderLoading ||
    serverOrder?.status === 'PRICE_REQUESTED' ||
    serverOrder?.status === 'INSERT_REQUESTED' ||
    // @ts-ignore   not yet generated in type
    serverOrder?.status === 'FIRE_REQUESTED' ||
    (serverOrderLoading && serverOrder?.status !== 'INSERT_ERROR'); // STOP INFINITE LOADING ON ERROR

  const isButtonLoading = isProcessing || serverOrderProcessing;

  const isButtonDisabled =
    isOfferValidationError ||
    !isPaymentReady ||
    isProcessing ||
    serverOrderProcessing ||
    !cartEntries.length ||
    !!serverOrderError ||
    (isDelivery && !validPhoneNumber) ||
    (selectedTip === 'other' && Number(otherTipValue) > MAX_TIP_AMOUNT_CENTS);

  const buttonText = getPlaceOrderButtonText(
    currentState,
    !!retryInfo,
    serverOrderLoading,
    serverOrder?.status
  );

  // Handle successful order insertion
  useEffect(() => {
    if (serverOrder?.status === 'INSERT_SUCCESSFUL') {
      router.dismissTo(`/order-confirmation/${serverOrder.rbiOrderId}?showToast=true`);
      resetPaymentMethodStore();
      emptyCart();
      resetPaymentFlowStore();
    }
  }, [
    serverOrder?.status,
    serverOrder?.rbiOrderId,
    setCurrentState,
    resetPaymentFlowStore,
    emptyCart,
    resetPaymentMethodStore,
  ]);

  const handleClearError = () => {
    setCurrentError(null);
    lastResultRef.current = null;
  };

  const handlePress = () => {
    if (retryInfo) {
      logPaymentRetry(
        logRBIEvent,
        `after failed ${retryInfo.failedStep}. Retrying from ${retryInfo.retryFromStep}`
      );
    } else {
      logCartPlaceOrder(logRBIEvent);
    }

    handlePlaceOrder({
      lastResultRef,
      orchestratorRef,
      buildPipelineContext,
      setIsProcessing,
      setShouldSkipPriceOrder,
      setCurrentError,
      logRBIEvent,
    });
  };

  return (
    <View style={[styles.container, styles.buttonContainer]}>
      {!!serverOrderError && <PricingAlert />}
      {!!currentError && <PaymentErrorAlert error={currentError} onClear={handleClearError} />}

      <Button
        testID={CART_PLACE_ORDER_BUTTON}
        size="lg"
        loading={isButtonLoading}
        disabled={isButtonDisabled}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={isButtonLoading ? 'Processing order' : buttonText}
        accessibilityHint={
          isButtonDisabled
            ? serverOrderError
              ? 'Order cannot be placed due to a pricing error. Please try again'
              : 'Please select a payment method and ensure your cart is not empty'
            : 'Places your order and processes payment'
        }
        accessibilityState={{ disabled: isButtonDisabled, busy: isButtonLoading }}
      >
        {!isButtonLoading && (
          <Button.Icon>
            <IconLocked />
          </Button.Icon>
        )}
        <Button.Text>{buttonText}</Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  container: {
    marginHorizontal: 'auto',
    padding: 16,
    width: '100%',
  },
});
